import { keyBy } from 'lodash';

/**
 * Groups an array of objects by a property of the objects.
 * Preserves the order of the original array in both the groups and the values within each group.
 * This is a custom version of groupBy that preserves the order of the original array which lodash's groupBy does not.
 * @param array The array to group.
 * @param key The property of the objects to group by.
 * @returns An array of objects, each containing a key and an array of values that share that key.
 */
export function groupBy<T>(array: T[], key: keyof T): { key: keyof T, values: T[] }[] {
  const result: { key: keyof T, values: T[] }[] = [];
  const groups: Dictionary<T[]> = {};

  array.forEach(item => {
    const groupKey = item[key] as string;

    if (!groups[groupKey]) {
      groups[groupKey] = [];
      result.push({ key: groupKey as keyof T, values: groups[groupKey] });
    }

    groups[groupKey].push(item);
  });

  return result;
}

/**
 * Merge multiple arrays of objects into a new array, using a property of the objects as the merge key.
 *
 * @param key The property to use as the merge key when combining the arrays.
 * @param arrays A list of arrays to merge.
 * @returns A new array containing the combination of all items in all arrays, merged using the provided merge key.
 */
export function mergeArraysByKey<T>(key: string, ...arrays: T[][]): T[] {
  return Object.values(
    Object.assign({}, ...arrays.map(array => keyBy(array, key)))
  );
}

/**
 * Returns the given array if the array is not empty. Otherwise, returns null.
 * @param value An array to check.
 * @returns The given array if the array is not empty. Otherwise, returns null.
 */
export function array(value: any[]): any[] | null {
  return value?.length > 0 ? value : null;
}

/**
 * Return the first value if the array is not empty. Otherwise, returns null.
 * @param value An array to get the first value from.
 * @returns The first value if the array is not empty. Otherwise, returns null.
 */
export function firstOrNull<T>(value: T[]): T {
  return value?.[0] ?? null;
}

/**
 * Return an array with the only item if the specified value is not empty. Otherwise, returns empty array.
 * @param value A string value to be used as the first item of a new array.
 * @returns An array with the only item if the specified value is not empty. Otherwise, returns empty array.
 */
export function makeArray<T>(value: T): T[] {
  return value ? [value] : [];
}

/**
 * Performs a binary search on an array returning the index of the item that satisfies the provided testing function.
 * @param array The array to search
 * @param predicate A function that determines if the item is found and which direction to search if not found.
 * It should return a number where:
 * - A negative value indicates that the current item is less than the item being searched for.
 * - A positive value indicates that the current item is greater than the item being searched for.
 * - Zero indicates that the current item is the item being searched for.
 * @returns The index of the found item, or -1 if no item is found.
 */
export function binarySearchIndex<T>(array: T[], predicate: (item: T) => number): number {
  let low = 0;
  let high = array.length - 1;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const result = predicate(array[mid]);

    // If the item was found then return the index
    if (result === 0) {
      return mid;
    }
    // Else if the item is less than the item being searched for then search the right half
    else if (result < 0) {
      low = mid + 1;
    }
    // Else search the left half
    else {
      high = mid - 1;
    }
  }

  // The item was not found
  return -1;
}

export function findLastIndex<T>(arr: T[], predicate: (value: T, index: number, array: T[]) => boolean): number {
  for (let i = arr.length - 1; i >= 0; i -= 1) {
    if (predicate(arr[i], i, arr)) {
      return i;
    }
  }
  return -1;
}

export function reverseMap<T, U>(arr: T[], callback: (value: T, index: number, array: T[]) => U): U[] {
  const result: U[] = [];
  for (let i = arr.length - 1; i >= 0; i -= 1) {
    result.push(callback(arr[i], i, arr));
  }
  return result;
}

export function toReversed<T>(arr: ReadonlyArray<T>): T[] {
  const result: T[] = [];
  for (let i = arr.length - 1; i >= 0; i -= 1) {
    result.push(arr[i]);
  }
  return result;
}
